/**
 * Farm & Acre Co - Fee Cart Protection
 *
 * Prevents manual removal of auto-added fees (print fees and SA levy)
 * and removes orphaned fees when the main product is removed from cart.
 * Syncs fee quantities when main product quantity changes using Order Group linking.
 */

(async function() {
  'use strict';

  // SPACEY: Import debug system from shared helpers with console.log fallback (prevents silent failures)
  const debug = window.CartSharedHelpers?.createDebugLogger('[FEE PROTECTION DEBUG]') || {
    log: function(message, data) {
      if (data !== null) {
        console.log('[FEE PROTECTION DEBUG]', message, data);
      } else {
        console.log('[FEE PROTECTION DEBUG]', message);
      }
    },
    error: function(message, error) {
      if (error !== null) {
        console.error('[FEE PROTECTION DEBUG]', message, error);
      } else {
        console.error('[FEE PROTECTION DEBUG]', message);
      }
    },
    warn: function(message, data) {
      if (data !== null) {
        console.warn('[FEE PROTECTION DEBUG]', message, data);
      } else {
        console.warn('[FEE PROTECTION DEBUG]', message);
      }
    }
  };

  const debugLog = debug.log;
  const debugError = debug.error;
  const debugWarn = debug.warn;

  debugLog('=== CART PROTECTION SYSTEM INITIALIZING ===');
  debugLog('Script loaded and initializing', {
    timestamp: new Date().toISOString(),
    hasSubscribe: typeof subscribe !== 'undefined',
    hasPubSubEvents: typeof PUB_SUB_EVENTS !== 'undefined',
    hasSharedHelpers: typeof window.CartSharedHelpers !== 'undefined'
  });

  // SPACEY: Wait for CartSharedHelpers to be available (prevents timing issues with defer)
  if (typeof window.CartSharedHelpers === 'undefined') {
    debugLog('Waiting for CartSharedHelpers to load...');
    await new Promise(function(resolve) {
      var checkInterval = setInterval(function() {
        if (typeof window.CartSharedHelpers !== 'undefined') {
          clearInterval(checkInterval);
          debugLog('CartSharedHelpers is now available');
          resolve();
        }
      }, 10);
      setTimeout(function() {
        clearInterval(checkInterval);
        resolve(); // Resolve anyway to continue
      }, 1000);
    });
  }

  // SPACEY: Global error handler to catch errors that might prevent event publishing
  window.addEventListener('error', (event) => {
    if (event.filename && event.filename.includes('cart.js')) {
      debugError('🚨 CART.JS ERROR DETECTED 🚨', {
        message: event.message,
        line: event.lineno,
        col: event.colno,
        error: event.error
      });
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    debugError('🚨 UNHANDLED PROMISE REJECTION 🚨', {
      reason: event.reason,
      promise: event.promise
    });
  });

  // SPACEY: Import fee variant IDs from shared helpers (now guaranteed to be available after wait)
  const ALL_SETUP_FEE_IDS = window.CartSharedHelpers.ALL_SETUP_FEE_IDS;
  const ALL_LINE_FEE_IDS = window.CartSharedHelpers.ALL_LINE_FEE_IDS;
  const ALL_SIDE_FEE_IDS = window.CartSharedHelpers.ALL_SIDE_FEE_IDS;
  const SA_LEVY_VARIANT_ID = window.CartSharedHelpers.SA_LEVY_VARIANT_ID;
  const PRINT_FEE_VARIANT_IDS = window.CartSharedHelpers.PRINT_FEE_VARIANT_IDS;
  const ALL_FEE_VARIANT_IDS = window.CartSharedHelpers.ALL_FEE_VARIANT_IDS;

  // SPACEY: Simple debounce timer and mutex for quantity sync (Phase 1)
  let syncDebounceTimer = null;
  let isSyncing = false;

  // Allflex SAP rebuild (08 s6, 21 Oct 2026): Allflex sells these tags only in
  // multiples (2027 price list / sheet). The PDP enforces it (form-validation.js),
  // but the cart steppers move by 1, so keep the line on its multiple here, the
  // same way the Pitch Black floor below does. Keyed by variant SKU (= Allflex
  // UIN, which never changes) and gated on vendor Allflex. A SKU not listed here
  // is never touched. RapID 899007 x20, Lightweight 899027 x10, FlexiTag printed
  // 899010 x25, Superflex 899008 x20.
  const ALLFLEX_QTY_STEPS = { '899007': 20, '899027': 10, '899010': 25, '899008': 20 };
  // Last quantity seen per cart line key, so a stepper "minus" (40 -> 39) goes
  // DOWN a step (20) and a "plus" (40 -> 41) goes UP a step (60). Unknown history
  // rounds up (never ship fewer tags than the customer asked for).
  const allflexLastQty = {};

  function allflexStepFor(item) {
    // Shearwell only: supplier pack sizes. Hardware and BOS singles stay untouched.
    if (item && /^Shearwell$/i.test(item.vendor || '')) {
      const steps = { 'shearwell-nlis-eid-sheep-goat-tags': 10, 'shearwell-nlis-visual-sheep-goat-tag': 10,
        'shearwell-management-visual-sheep-goat-tag': 10, 'shearwell-nlis-met-cattle-tag': 20 };
      return steps[item.handle] || 0;
    }
    if (!item || item.vendor !== 'Allflex') return 0;
    return ALLFLEX_QTY_STEPS[String(item.sku || '')] || 0;
  }

  function allflexOnStepQty(item) {
    const step = allflexStepFor(item);
    const qty = item.quantity;
    if (!step || qty < 1 || qty % step === 0) return qty;
    const prev = allflexLastQty[item.key];
    if (prev && qty < prev) return Math.max(step, Math.floor(qty / step) * step);
    return Math.ceil(qty / step) * step;
  }

  function initFeeProtection() {
    debugLog('=== INITIALIZING FEE PROTECTION ===');

    // SPACEY: Clean fetch interception pattern - doesn't block Dawn
    // Processes our business logic asynchronously after Dawn completes
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const url = args[0];

      // Let Dawn's fetch complete first
      const response = await originalFetch.apply(this, args);

      // Process cart mutations asynchronously (don't block Dawn)
      if (typeof url === 'string') {
        const isCartMutation = url.includes('/cart/change') ||
                              url.includes('/cart/update') ||
                              url.includes('/cart/add');

        if (isCartMutation) {
          debugLog('🟢 [CART INTERCEPT] Cart mutation detected:', url);

          // Process asynchronously - don't block Dawn's response
          (async () => {
            try {
              let cart;

              // For /cart/add, fetch full cart state (add.js doesn't return it)
              if (url.includes('/cart/add')) {
                const cartResponse = await fetch('/cart.js');
                cart = await cartResponse.json();
              } else {
                // For /cart/change and /cart/update, clone response
                cart = await response.clone().json();
              }

              debugLog('📦 [CART INTERCEPT] Cart state:', {
                itemCount: cart.items?.length,
                items: cart.items?.map(i => ({
                  title: i.title,
                  variant_id: i.variant_id,
                  quantity: i.quantity
                }))
              });

              // SPACEY: Sync fees and remove orphans
              const cartModified = await syncFeeQuantities();

              if (cartModified) {
                debugLog('📡 [CART INTERCEPT] Cart was modified, refreshing all sections');
                await refreshAllCartSections();
              } else {
                debugLog('📡 [CART INTERCEPT] No modifications needed');
              }

              // SPACEY: Publish event for other subscribers (fee-auto-add, etc)
              if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'fee-protection',
                  cartData: cart
                });
              }
            } catch (e) {
              debugError('❌ [CART INTERCEPT] Processing failed:', e);
            }
          })();
        }
      }

      // Return response to Dawn immediately (no blocking)
      return response;
    };

    debugLog('✓ Fetch interception active (async, non-blocking)');

    // SPACEY: Subscribe to cartUpdate events to re-apply fee protection after Dawn updates
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        debugLog('📡 [CART INTERCEPT] cartUpdate event, re-applying fee protection');
        protectFeeItems();
      });
      debugLog('✓ Subscribed to cartUpdate events for fee protection');
    }

    // SPACEY: Initial protection and sync on page load
    debugLog('Calling protectFeeItems() on initial load');
    protectFeeItems();

    debugLog('Calling syncFeeQuantities() on initial load');
    syncFeeQuantities();

    debugLog('Fee cart protection initialized (print fees + SA levy)');
    debugLog('=== FEE PROTECTION INITIALIZATION COMPLETE ===');
  }

  // SPACEY: Removed backup removal detection - fetch interception handles all cases
  // Previous setupBackupRemovalListeners() removed as redundant
  // Fetch interception detects 100% of cart modifications in both drawer and page

  function protectFeeItems() {
    debugLog('=== PROTECTING FEE ITEMS ===');
    
    // Find all cart items (both in cart drawer and cart page)
    const cartItems = document.querySelectorAll('cart-items cart-item, .cart-item, tr.cart-item, [class*="cart__item"]');
    debugLog('Found cart items:', cartItems.length);
    debugLog('Fee variant IDs being checked:', ALL_FEE_VARIANT_IDS);

    let protectedCount = 0;
    cartItems.forEach(item => {
      // Check if this is a fee item by checking the variant ID
      const variantIdElement = item.querySelector('[data-quantity-variant-id], [name*="updates"]');
      if (!variantIdElement) {
        debugLog('Item has no variant ID element, skipping');
        return;
      }

      // Get variant ID from either data attribute or name attribute
      let variantId = variantIdElement.getAttribute('data-quantity-variant-id');
      if (!variantId) {
        variantId = variantIdElement.name?.match(/updates\[(\d+)\]/)?.[1];
      }
      if (!variantId) {
        debugLog('Could not extract variant ID from item');
        return;
      }

      debugLog('Checking item with variant ID:', variantId);

      // SPACEY: Check for fee variant or button pair (both should have quantity controls hidden)
      // Properties in cart DOM are typically shown in a list, check for _Button pair property
      const propertiesElement = item.querySelector('.cart-item__properties, .product-option, [data-cart-item-property]');
      const hasButtonPairProperty = propertiesElement && propertiesElement.textContent.includes('_Button pair');

      if (ALL_FEE_VARIANT_IDS.includes(variantId) || hasButtonPairProperty) {
        debugLog('✓ Item is a FEE variant or BUTTON PAIR, applying protection');
        protectedCount++;

        // Hide the entire quantity cell (which contains quantity controls and remove button)
        const quantityCell = item.querySelector('.cart-item__quantity');
        if (quantityCell) {
          debugLog('  - Hiding quantity cell for protected item');
          quantityCell.style.display = 'none';
        } else {
          debugLog('  - No quantity cell found');
        }
      } else {
        debugLog('✗ Item is NOT a protected variant, no protection');
      }
    });

    debugLog('Total fee items protected:', protectedCount);
    debugLog('=== PROTECTING FEE ITEMS COMPLETE ===');
  }

  /**
   * Sync fee quantities and remove orphaned fees
   * Uses Order Group linking to identify related items and detect orphans
   * SPACEY: Combined sync and orphan detection in one pass (single cart fetch)
   * @returns {boolean} True if cart was modified, false otherwise
   */
  async function syncFeeQuantities() {
    debugLog('=== SYNCING FEE QUANTITIES AND REMOVING ORPHANS ===');

    try {
      debugLog('Fetching current cart state...');
      const response = await fetch('/cart.js');
      const cart = await response.json();
      debugLog('Cart items count:', cart.items.length);
      debugLog('Cart items:', cart.items.map(item => ({
        title: item.title,
        variantId: item.variant_id,
        orderGroup: item.properties?.['_Order Group']
      })));

      // Allflex SAP rebuild (08 s6): on-step quantity for every Allflex multiples
      // line, grouped or not (a RapID line with no fees has no _Order Group).
      const allflexFixed = {};
      for (const item of cart.items) {
        const fixed = allflexOnStepQty(item);
        if (fixed !== item.quantity) {
          debugLog('Allflex multiple: "' + item.title + '" ' + item.quantity + ' → ' + fixed);
          allflexFixed[item.key] = fixed;
        }
      }

      // SPACEY: Group items by Order Group ID (used for both sync and orphan detection)
      const orderGroups = {};
      for (const item of cart.items) {
        const orderGroupId = item.properties?.['_Order Group'];
        if (!orderGroupId) {
          debugLog(`⚠️ Item "${item.title}" (variant: ${item.variant_id}) has NO _Order Group property`);
          debugLog(`   All properties:`, item.properties);
          continue;
        }

        if (!orderGroups[orderGroupId]) {
          debugLog(`📦 Creating new order group: ${orderGroupId}`);
          orderGroups[orderGroupId] = { mainProduct: null, fees: [] };
        }

        const variantId = item.variant_id.toString();
        // SPACEY: Include items with _Button pair property (button pairs sync with main product quantity)
        const isButtonPair = item.properties?.['_Button pair'] === 'true';

        if (ALL_FEE_VARIANT_IDS.includes(variantId) || isButtonPair) {
          debugLog(`  → 🆓 Adding to fees array: ${item.title} (variant: ${variantId}, key: ${item.key}, qty: ${item.quantity})`);
          orderGroups[orderGroupId].fees.push(item);
        } else {
          debugLog(`  → 🛍️ Setting as main product: ${item.title} (variant: ${variantId}, key: ${item.key}, qty: ${item.quantity})`);
          orderGroups[orderGroupId].mainProduct = item;
        }
      }

      debugLog(`✅ Order groups created: ${Object.keys(orderGroups).length}`);
      debugLog(`📋 Order group summary:`, Object.keys(orderGroups).map(groupId => ({
        id: groupId,
        hasMain: !!orderGroups[groupId].mainProduct,
        mainTitle: orderGroups[groupId].mainProduct?.title,
        mainQty: orderGroups[groupId].mainProduct?.quantity,
        feeCount: orderGroups[groupId].fees.length,
        feeTitles: orderGroups[groupId].fees.map(f => ({ title: f.title, qty: f.quantity, key: f.key }))
      })));

      // SPACEY: Collect quantity updates and identify orphans in same pass
      const updates = {};
      const orphansToRemove = [];
      Object.assign(updates, allflexFixed);

      // For each order group, sync fee quantities and detect orphans
      for (const groupId of Object.keys(orderGroups)) {
        const group = orderGroups[groupId];

        // SPACEY: Check if this group has a main product (orphan detection)
        if (!group.mainProduct) {
          // SPACEY: No main product = orphaned fees (mark for removal)
          debugLog(`🚨 ORPHAN DETECTED: Order group ${groupId} has NO main product`);
          debugLog(`    Orphaned fees: ${group.fees.length}`, group.fees.map(f => ({ title: f.title, key: f.key, qty: f.quantity })));
          orphansToRemove.push(...group.fees);
          continue;
        }

        // SPACEY: Main product exists, sync fee quantities
        const mainQty = group.mainProduct.quantity;
        debugLog(`Order group ${groupId} - Main product: "${group.mainProduct.title}" (qty: ${mainQty})`);
        debugLog(`Order group ${groupId} - Fee items: ${group.fees.length}`);

        // Pitch Black MOQ floor (supplier requirement, 2026-08 hotfix): the cart
        // page/drawer steppers only know the variant rule (min 1), so a customer
        // can drop a Pitch Black tag line below 100 after adding. Restore the floor
        // in the same batched update; the fee sync below follows the corrected qty.
        const PITCH_BLACK_MOQ = 100;
        // Allflex multiples (above): fees follow the corrected quantity.
        let effectiveMainQty = Object.prototype.hasOwnProperty.call(allflexFixed, group.mainProduct.key)
          ? allflexFixed[group.mainProduct.key]
          : mainQty;
        if (group.mainProduct.properties?.['Print Method'] === 'Pitch Black' &&
            mainQty >= 1 && mainQty < PITCH_BLACK_MOQ) {
          debugLog('Pitch Black MOQ: bumping "' + group.mainProduct.title + '" ' + mainQty + ' → ' + PITCH_BLACK_MOQ);
          updates[group.mainProduct.key] = PITCH_BLACK_MOQ;
          effectiveMainQty = PITCH_BLACK_MOQ;
        }

        for (const feeItem of group.fees) {
          const feeType = feeItem.properties?.['_Fee type'];

          // Setup fees are always qty=1, per-tag fees (line, levy) match main product qty
          const expectedQty = (feeType === 'setup') ? 1 : effectiveMainQty;

          debugLog(`  Checking fee: "${feeItem.title}" (current: ${feeItem.quantity}, expected: ${expectedQty}, type: ${feeType})`);

          if (feeItem.quantity !== expectedQty) {
            debugLog('Queuing fee qty sync: ' + feeItem.title + ' ' + feeItem.quantity + ' → ' + expectedQty);
            updates[feeItem.key] = expectedQty;
          } else {
            debugLog(`  ✓ Fee quantity already correct`);
          }
        }
      }

      debugLog('Total quantity updates queued:', Object.keys(updates).length);
      debugLog('Total orphans to remove:', orphansToRemove.length);

      // Remember where each Allflex multiples line ends up, for the next
      // stepper direction check.
      for (const item of cart.items) {
        if (allflexStepFor(item)) {
          allflexLastQty[item.key] = Object.prototype.hasOwnProperty.call(updates, item.key) ? updates[item.key] : item.quantity;
        }
      }

      debugLog('Total quantity updates queued:', Object.keys(updates).length);

      // Batch update all fees in a single API call
      if (Object.keys(updates).length > 0) {
        debugLog('=== SENDING CART UPDATE REQUEST ===');
        debugLog('Updates to send:', updates);

        debugLog('Batch syncing ' + Object.keys(updates).length + ' fee quantities...');

        try {
          // SPACEY: Use cart operation queue to serialize all cart updates (Fix 2)
          // This ensures all cart operations run one at a time (FIFO)
          let updatedCart;

          // SPACEY: Check if CartOperationQueue is available (created by cart-operation-queue.js)
          if (typeof window.CartOperationQueue !== 'undefined' && window.CartOperationQueue.cartUpdate) {
            debugLog('Using CartOperationQueue for serialized cart update');
            updatedCart = await window.CartOperationQueue.cartUpdate(updates);
          } else {
            // Fallback to direct fetch if queue not available
            debugWarn('CartOperationQueue not available, using direct fetch (not serialized)');
            const updateResponse = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            });

            if (!updateResponse.ok) {
              throw new Error('Cart update failed: ' + updateResponse.statusText);
            }

            updatedCart = await updateResponse.json();
          }

          debugLog('Cart update response status: 200');
          debugLog('Updated cart items:', updatedCart.items.map(i => ({
            title: i.title,
            quantity: i.quantity,
            key: i.key
          })));

          // SPACEY: Verify each fee was updated correctly (Fix 3 - response verification)
          let allVerified = true;
          for (const [key, expectedQty] of Object.entries(updates)) {
            const updatedItem = updatedCart.items.find(i => i.key === key);

            if (!updatedItem) {
              debugError('!!! ITEM NOT FOUND in response !!!', { key, expectedQty });
              allVerified = false;
              continue;
            }

            if (updatedItem.quantity !== expectedQty) {
              debugError('!!! QUANTITY MISMATCH !!!', {
                title: updatedItem.title,
                key: key,
                expected: expectedQty,
                actual: updatedItem.quantity
              });
              allVerified = false;
            } else {
              debugLog('✓ Verified: ' + updatedItem.title + ' quantity = ' + expectedQty);
            }
          }

          if (allVerified) {
            debugLog('✓ All fee quantities synced and verified');
          } else {
            debugWarn('Some fee quantities failed to sync correctly');
          }
        } catch (error) {
          debugError('!!! ERROR IN CART UPDATE REQUEST !!!', error);
          debugError('Error stack:', error.stack);
        }
      } else {
        debugLog('No quantity updates needed, all fees are in sync');
      }

      // SPACEY: Remove orphaned fees (fees without main product in same Order Group)
      if (orphansToRemove.length > 0) {
        debugLog('=== 🗑️ REMOVING ORPHANED FEES ===');
        debugLog(`Orphaned fees found: ${orphansToRemove.length}`);
        debugLog(`Full orphan list:`, orphansToRemove.map(o => ({
          title: o.title,
          key: o.key,
          variant_id: o.variant_id,
          orderGroup: o.properties?.['_Order Group'],
          feeType: o.properties?.['_Fee type']
        })));

        for (const orphan of orphansToRemove) {
          debugLog(`🗑️  Removing orphaned fee: "${orphan.title}"`);
          debugLog(`    Order Group: ${orphan.properties?.['_Order Group']}`);
          debugLog(`    Fee Key: ${orphan.key}`);
          debugLog(`    Fee Variant: ${orphan.variant_id}`);

          try {
            // SPACEY: Use CartOperationQueue for serialized cart operations
            if (typeof window.CartOperationQueue !== 'undefined' && window.CartOperationQueue.cartChange) {
              debugLog(`    Using CartOperationQueue to remove...`);
              await window.CartOperationQueue.cartChange(orphan.key, 0);
            } else {
              debugLog(`    Using direct fetch to remove...`);
              // Fallback to direct fetch
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: orphan.key, quantity: 0 })
              });
            }
            debugLog(`  ✅ Removed orphaned fee: "${orphan.title}"`);
          } catch (error) {
            debugError(`  ❌ Failed to remove orphaned fee: "${orphan.title}"`, error);
          }
        }

        debugLog(`✅ All orphaned fees removed (${orphansToRemove.length} total)`);
      } else {
        debugLog('✅ No orphaned fees found - all fees have valid parent products');
      }

      // SPACEY: Return true if cart was modified (for UI refresh trigger)
      const cartWasModified = Object.keys(updates).length > 0 || orphansToRemove.length > 0;

      if (cartWasModified) {
        debugLog('✓ Cart was modified (fees synced or orphans removed)');
      } else {
        debugLog('✓ No cart modifications needed');
      }

      return cartWasModified;
    } catch (error) {
      debugError('!!! ERROR SYNCING FEES OR REMOVING ORPHANS !!!', error);
      debugError('Error stack:', error.stack);
      return false; // Return false on error
    } finally {
      debugLog('=== SYNC AND ORPHAN REMOVAL COMPLETE ===');
    }
  }

  /**
   * SPACEY: Refresh cart UI using shared helper (Dawn's Section Rendering API pattern)
   * This updates ALL cart contexts (drawer, page, icon bubble) correctly
   * Dawn's onCartUpdate() is incomplete - this implements the full update
   */
  async function refreshAllCartSections() {
    // SPACEY: Use shared helper, passing debug logger for consistent logging
    if (window.CartSharedHelpers?.refreshAllCartSections) {
      await window.CartSharedHelpers.refreshAllCartSections(debug);
      // SPACEY: Re-apply fee protection after DOM updates
      protectFeeItems();
    } else {
      debugError('CartSharedHelpers not available - cannot refresh cart sections');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFeeProtection);
  } else {
    initFeeProtection();
  }

})();
